import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import queriesApi from '../api/queries';
import QueryResults from '../components/query/QueryResults';

const QueryView = () => {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [queryDetails, setQueryDetails] = useState(null);
  const [queryResults, setQueryResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [cancellingQuery, setCancellingQuery] = useState(false);
  
  // Load query details
  useEffect(() => {
    const fetchQueryDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const details = await queriesApi.getQueryDetails(queryId);
        setQueryDetails(details);
        
        // If query is async and completed, fetch the results
        if (details.is_async && (details.status === 'completed' || details.status === 'COMPLETED')) {
          console.log("Query details for async completed query:", details);
          // Use job_id if available, otherwise fall back to id
          const jobId = details.job_id || details.id;
          await fetchQueryResults(jobId);
        } else if (!details.is_async) {
          // For non-async queries, there's no way to retrieve results
          // They need to be re-executed
          console.log('Non-async query results not available - need to re-run query');
        }
      } catch (err) {
        console.error('Failed to fetch query details:', err);
        setError('Failed to load query details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    if (queryId) {
      fetchQueryDetails();
    }
  }, [queryId]);

  // Polling effect for running queries
  useEffect(() => {
    let intervalId;
    
    if (queryDetails && (queryDetails.status === 'running' || queryDetails.status === 'pending')) {
      intervalId = setInterval(async () => {
        try {
          const updatedDetails = await queriesApi.getQueryDetails(queryId);
          setQueryDetails(updatedDetails);
          
          // Stop polling if query is no longer running
          if (updatedDetails.status !== 'running' && updatedDetails.status !== 'pending') {
            // If query completed, fetch results
            if (updatedDetails.is_async && (updatedDetails.status === 'completed' || updatedDetails.status === 'COMPLETED')) {
              const jobId = updatedDetails.job_id || updatedDetails.id;
              await fetchQueryResults(jobId);
            }
          }
        } catch (err) {
          console.error('Failed to poll query status:', err);
        }
      }, 5000); // Poll every 5 seconds
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [queryDetails, queryId]);
  
  // Function to fetch query results
  const fetchQueryResults = async (jobId) => {
    try {
      setLoadingResults(true);
      console.log(`Loading results for job ${jobId}`);
      const results = await queriesApi.getQueryResults(jobId);
      console.log('Results loaded successfully:', results);
      
      // If the format is missing, set default from query details
      if (results && !results.format && queryDetails && queryDetails.format) {
        results.format = queryDetails.format;
      }
      
      setQueryResults(results);
    } catch (err) {
      console.error('Failed to fetch query results:', err);
      setError(`Failed to load query results: ${err.message}`);
    } finally {
      setLoadingResults(false);
    }
  };
  
  // Function to run the query again
  const runQueryAgain = () => {
    if (queryDetails) {
      navigate('/query', { state: { query: queryDetails.query_text } });
    }
  };
  
  // Function to cancel a running query
  const cancelQuery = async () => {
    try {
      setCancellingQuery(true);
      const jobId = queryDetails.job_id || queryDetails.id;
      await queriesApi.cancelQuery(jobId);
      
      // Update the query status immediately
      setQueryDetails(prev => ({
        ...prev,
        status: 'cancelled',
        error: 'Query cancelled by user'
      }));
      
      setError('Query cancelled successfully');
    } catch (err) {
      console.error('Failed to cancel query:', err);
      setError(`Failed to cancel query: ${err.message}`);
    } finally {
      setCancellingQuery(false);
    }
  };
  
  if (loading) {
    return (
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8 text-center">
        <div className="inline-block w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="mt-2 text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Loading query details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="p-6 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 rounded-md">
          <h2 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">Error</h2>
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <div className="mt-4">
            <Link to="/queries/history" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              ← Back to Query History
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (!queryDetails) {
    return (
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="p-6 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-400 dark:border-yellow-800 rounded-md">
          <h2 className="text-xl font-bold text-yellow-700 dark:text-yellow-400 mb-2">Query Not Found</h2>
          <p className="text-yellow-700 dark:text-yellow-400">The requested query could not be found.</p>
          <div className="mt-4">
            <Link to="/queries/history" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              ← Back to Query History
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Query Details</h1>
        
        <div>
          <Link to="/queries/history" className="px-3 py-2 mr-2 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-background dark:hover:bg-ui-dark-background/50 transition-colors">
            ← Back to History
          </Link>
          
          {queryDetails && (queryDetails.status === 'running' || queryDetails.status === 'pending' || queryDetails.status === 'queued') && (
            <button
              onClick={cancelQuery}
              disabled={cancellingQuery}
              className="px-3 py-2 mr-2 text-sm text-white bg-red-600 dark:bg-red-500 rounded-md hover:bg-red-700 dark:hover:bg-red-400 transition-colors disabled:opacity-50"
            >
              {cancellingQuery ? 'Cancelling...' : 'Cancel Query'}
            </button>
          )}
          
          <button
            onClick={runQueryAgain}
            className="px-3 py-2 text-sm text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors"
          >
            Run Again
          </button>
        </div>
      </div>
      
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md p-6 mb-6 border border-ui-light-border dark:border-ui-dark-border transition-colors">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">Query Information</h2>
            
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Status</div>
                <div className="mt-1 flex items-center">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full
                    ${queryDetails.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                    queryDetails.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' :
                    queryDetails.status === 'cancelled' ? 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400' :
                    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'}`}>
                    {queryDetails.status}
                  </span>
                  {queryDetails.status === 'running' && (
                    <div className="ml-2 w-4 h-4 text-yellow-600 dark:text-yellow-400 animate-spin">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Execution Time</div>
                <div className="mt-1 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  {queryDetails.execution_time_ms ? `${queryDetails.execution_time_ms} ms` : 'N/A'}
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Created At</div>
                <div className="mt-1 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  {new Date(queryDetails.created_at).toLocaleString()}
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Type</div>
                <div className="mt-1 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  {queryDetails.is_async ? 'Asynchronous' : 'Synchronous'}
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Format</div>
                <div className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full
                    ${queryDetails.format === 'csv' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400'}`}>
                    {queryDetails.format ? queryDetails.format.toUpperCase() : 'PARQUET'}
                  </span>
                </div>
              </div>
              
              {queryDetails.is_async && queryDetails.job_id && (
                <div>
                  <div className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Job ID</div>
                  <div className="mt-1 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
                    {queryDetails.job_id}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div>
            <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">Query Text</h2>
            <div className="p-3 bg-ui-light-background dark:bg-ui-dark-background rounded-md font-mono text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary whitespace-pre-wrap overflow-auto max-h-48 transition-colors">
              {queryDetails.query_text}
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-6">
        <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">Query Results</h2>
        
        {loadingResults ? (
          <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border transition-colors">
            <div className="inline-block w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="mt-2 text-ui-light-text-primary dark:text-ui-dark-text-primary">Loading query results...</p>
            <p className="mt-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              Format: <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ml-1 ${
                queryDetails.format === 'csv' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400'
              }`}>
                {queryDetails.format ? queryDetails.format.toUpperCase() : 'PARQUET'}
              </span>
            </p>
          </div>
        ) : queryDetails.is_async && queryDetails.status === 'completed' ? (
          queryResults ? (
            queryResults.error ? (
              <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border transition-colors">
                <div className={`p-4 mb-4 border rounded-md ${queryResults.resultsExpired ? 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 border-yellow-400 dark:border-yellow-800' : 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 border-red-400 dark:border-red-800'}`}>
                  <p className="font-medium">
                    {queryResults.resultsExpired 
                      ? "Query results have expired" 
                      : "Error loading query results"}
                  </p>
                  <p className="mt-1">{queryResults.message || 'Failed to load data from server'}</p>
                  
                  {queryResults.resultsExpired && (
                    <div className="mt-3 text-sm">
                      <p>Results for asynchronous queries are stored temporarily and may expire after some time.</p>
                      <p>To see the data again, you'll need to run the query again.</p>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  {!queryResults.resultsExpired && (
                    <button
                      onClick={() => fetchQueryResults(queryDetails.job_id || queryDetails.id)}
                      className="px-4 py-2 mr-3 text-sm text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors"
                    >
                      Try Loading Results Again
                    </button>
                  )}
                  
                  <button
                    onClick={runQueryAgain}
                    className="px-4 py-2 text-sm text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors"
                  >
                    Run Query Again
                  </button>
                </div>
              </div>
            ) : (
              <QueryResults results={queryResults} />
            )
          ) : (
            <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border transition-colors">
              <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                {error ? error : "No results available. The results may have expired or been deleted."}
              </p>
              <div className="mt-4">
                <button
                  onClick={() => fetchQueryResults(queryDetails.job_id || queryDetails.id)}
                  className="px-4 py-2 mr-3 text-sm text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors"
                >
                  Try Loading Results Again
                </button>
                
                <button
                  onClick={runQueryAgain}
                  className="px-4 py-2 text-sm text-white bg-green-600 dark:bg-green-500 rounded-md hover:bg-green-700 dark:hover:bg-green-400 transition-colors"
                >
                  Run Query Again
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border transition-colors">
            <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              {queryDetails.status === 'failed' ? (
                "This query failed to execute. View the error details above and try running it again with modifications."
              ) : queryDetails.status === 'cancelled' ? (
                "This query was cancelled. You can run it again if needed."
              ) : queryDetails.status === 'running' ? (
                <span className="flex items-center justify-center">
                  <div className="w-4 h-4 mr-2 text-yellow-600 dark:text-yellow-400 animate-spin">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  This query is still running. Checking status automatically...
                </span>
              ) : !queryDetails.is_async ? (
                "Results are not stored for synchronous queries. Please run the query again to see results."
              ) : (
                "No results available for this query."
              )}
            </p>
            <div className="mt-4">
              <button
                onClick={runQueryAgain}
                className="px-4 py-2 text-sm text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors"
              >
                Run Query Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryView;